import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, ArrowRight } from "lucide-react";
import axios from "axios";
import { API, loadLocalFitKey, isPreviewId } from "@/lib/fitkey";
import BrandRecommendation from "@/components/fittin/BrandRecommendation";

// The mock product this demo sits on. Uses a real seeded brand slug so the
// compact widget gets live data from POST /api/recommend.
const DEMO = {
  brandSlug: "nike",
  brandLabel: "Nike",
  product: "Air Zoom Pegasus 41",
  color: "Black / White",
  price: "\u20ac 139.99",
  url: "shop.example.com / nike / pegasus-41",
};

// LIVE "EMBEDDED WIDGET" DEMO \u2014 shows exactly what a shopper sees when Fittin
// is embedded on a real product page. If the visitor already has a FitKey
// (cached foot length) we pre-fill and show the recommendation instantly;
// otherwise a compact foot-length input drives a live /api/recommend call.
export default function IntegrationDemo() {
  const [footLengthCm, setFootLengthCm] = useState(null);
  const [fromFitId, setFromFitId] = useState(false);
  const [inputVal, setInputVal] = useState("26.2");

  // Pre-fill from the visitor's own FitKey (kept on-device after check-in): the
  // backend returns the foot length it holds for that key. Silent on any failure.
  useEffect(() => {
    const fk = loadLocalFitKey();
    if (!fk?.fitkey_id || isPreviewId(fk.fitkey_id)) return undefined;
    let alive = true;
    axios
      .get(`${API}/fitkey/${encodeURIComponent(fk.fitkey_id)}`, { timeout: 8000 })
      .then((r) => {
        const len = r?.data?.foot_length_cm;
        if (alive && len) {
          setFootLengthCm(Number(len));
          setFromFitId(true);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const v = parseFloat(inputVal);
    if (!Number.isFinite(v) || v < 15 || v > 40) return;
    setFootLengthCm(v);
    setFromFitId(false);
  };

  return (
    <section
      id="integration"
      data-testid="integration-section"
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
              <span className="h-px w-8 bg-black/40" />
              Live demo
            </div>
          </div>
          <h2
            data-testid="integration-headline"
            className="md:col-span-8 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] font-medium leading-[1.02]"
          >
            This is what your shoppers see.{" "}
            <span className="text-neutral-400">Fittin, embedded on the product page.</span>
          </h2>
        </div>

        {/* Mock product-page frame */}
        <div className="relative mx-auto max-w-3xl rounded-[28px] bg-white border border-black/10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)] overflow-hidden">
          <div className="flex items-center gap-2 px-5 h-10 border-b border-black/5 bg-neutral-50">
            <span className="h-2.5 w-2.5 rounded-full bg-black/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-black/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-black/10" />
            <div className="mx-auto text-[11px] text-neutral-500 font-mono truncate">
              {DEMO.url}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Placeholder product image */}
            <div className="relative aspect-square md:aspect-auto bg-gradient-to-br from-neutral-100 to-neutral-200 border-b md:border-b-0 md:border-r border-black/5 min-h-[220px]">
              <div className="absolute inset-0 grid place-items-center">
                <ShoppingBag className="h-20 w-20 text-black/20" strokeWidth={1} />
              </div>
              <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                {DEMO.product} · {DEMO.color}
              </div>
            </div>

            {/* Product details + embedded Fittin widget */}
            <div className="p-7 md:p-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                {DEMO.brandLabel}
              </div>
              <h3 className="mt-2 text-2xl tracking-tight font-medium">
                {DEMO.product}
              </h3>
              <div className="mt-1 text-[15px] text-neutral-600">{DEMO.price}</div>

              {/* The embedded widget slot \u2014 exactly where it sits on a real PDP */}
              <div className="mt-6">
                {footLengthCm ? (
                  <div data-testid="demo-widget-wrap">
                    {fromFitId && (
                      <div className="mb-2 flex items-center gap-2">
                        <span
                          data-testid="demo-from-fitid"
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-emerald-700"
                        >
                          <Sparkles className="h-2.5 w-2.5" />
                          From your FitKey
                        </span>
                      </div>
                    )}
                    <BrandRecommendation
                      footLengthCm={footLengthCm}
                      primaryBrand={DEMO.brandSlug}
                      testid="demo-widget"
                    />
                    <button
                      type="button"
                      data-testid="demo-change-foot"
                      onClick={() => {
                        setFootLengthCm(null);
                        setFromFitId(false);
                      }}
                      className="mt-2 text-[11.5px] text-neutral-400 hover:text-[#0A0A0A] transition-colors"
                    >
                      {fromFitId ? "Not you? Enter a foot length" : "Change foot length"}
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={submit}
                    data-testid="demo-foot-form"
                    className="rounded-2xl border border-black/10 bg-[#FAFAFA] p-4"
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                      <Sparkles className="h-3 w-3" />
                      Your Fittin size
                    </div>
                    <label className="mt-2 block text-[12.5px] text-neutral-600">
                      Enter your foot length to see your size here.
                    </label>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          step="0.1"
                          min="15"
                          max="40"
                          value={inputVal}
                          onChange={(e) => setInputVal(e.target.value)}
                          data-testid="demo-foot-input"
                          className="w-full bg-white text-[15px] text-[#0A0A0A] border border-black/15 rounded-lg pl-3 pr-9 py-2 focus:border-black/70 focus:outline-none transition-colors"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400">
                          cm
                        </span>
                      </div>
                      <button
                        type="submit"
                        data-testid="demo-foot-submit"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white text-[13px] px-4 py-2.5 hover:bg-neutral-800 transition-all active:scale-[0.98]"
                      >
                        Show my size
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Disabled add-to-cart \u2014 this is a demo, not a live shop */}
              <button
                type="button"
                disabled
                data-testid="demo-add-to-cart"
                className="mt-5 w-full h-12 rounded-full bg-[#0A0A0A] text-white text-[14px] opacity-40 cursor-not-allowed"
              >
                Add to cart
              </button>
            </div>
          </div>
        </div>

        <p
          data-testid="integration-caption"
          className="mt-6 text-center text-[13px] text-neutral-500"
        >
          Live demo &mdash; the compact Fittin widget embeds into any product-page sidebar.
        </p>
      </div>
    </section>
  );
}
